import React, { useMemo } from "react";

const PetDemandPanel = ({ overview, siteTypes, loading }) => {
  const { withPets, noPets } = useMemo(() => {
    if (!overview?.length) return { withPets: null, noPets: null };
    return {
      withPets: overview.find((r) => r.pet_status === "With Pets") || null,
      noPets: overview.find((r) => r.pet_status === "No Pets") || null,
    };
  }, [overview]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="flex gap-3">
          <div className="flex-1 h-16 bg-gray-200 dark:bg-gray-800 rounded" />
          <div className="flex-1 h-16 bg-gray-200 dark:bg-gray-800 rounded" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-6 bg-gray-200 dark:bg-gray-800 rounded" style={{ width: `${70 - i * 15}%` }} />
        ))}
      </div>
    );
  }

  if (!withPets || withPets.sessions === 0) {
    return (
      <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
        No pet data for this period.
      </div>
    );
  }

  const totalSessions = (withPets?.sessions || 0) + (noPets?.sessions || 0);
  const petPct = totalSessions > 0 ? Math.round((withPets.sessions / totalSessions) * 1000) / 10 : 0;
  const petConv = withPets?.conversion_pct || 0;
  const noPetConv = noPets?.conversion_pct || 0;
  const convBetter = petConv >= noPetConv;

  return (
    <div>
      {/* KPI cards */}
      <div className="flex gap-3 mb-4">
        <div className="flex-1 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {withPets.sessions} <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({petPct}%)</span>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">With pets</div>
        </div>
        <div className="flex-1 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          <div className={`text-xl font-bold ${convBetter ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
            {petConv}% <span className="text-sm font-normal text-gray-500 dark:text-gray-400">vs {noPetConv}%</span>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Pet conversion vs. non-pet</div>
        </div>
      </div>

      {/* Site type breakdown */}
      {siteTypes?.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            Site types pet owners search
          </div>
          {(() => {
            const maxSess = siteTypes[0]?.pet_sessions || 1;
            return siteTypes.map((s, idx) => (
              <div key={s.site_type} className={`max-[499px]:rounded-md max-[499px]:px-2 max-[499px]:py-2 ${idx % 2 === 0 ? "max-[499px]:bg-gray-50 dark:max-[499px]:bg-gray-800/40" : ""}`}>
                <div className="flex items-center justify-between mb-0.5 max-[499px]:flex-col max-[499px]:items-start max-[499px]:gap-0.5">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[140px] max-[499px]:max-w-full">{s.site_type}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {s.pet_sessions} sess · {s.pet_bookings} bkd · {s.conversion_pct}%
                  </span>
                </div>
                <div
                  className="bg-gray-100 dark:bg-gray-800 rounded-full h-3 overflow-hidden"
                  title={`${s.site_type}\n${s.pet_sessions} pet sessions · ${s.pet_bookings} booked\n${s.conversion_pct}% conversion`}
                >
                  <div
                    className="h-full rounded-full bg-[#32B1FF]/30 transition-all"
                    style={{ width: `${(s.pet_sessions / maxSess) * 100}%` }}
                  />
                </div>
              </div>
            ));
          })()}
        </div>
      )}
    </div>
  );
};

export default PetDemandPanel;
